/* A1.6 — Daily Action Digest : faits journalisés uniquement (aucun envoi externe). */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Newspaper, Loader2, X } from "lucide-react";

const fmtMoney = (v, cur = "CHF") => v == null ? "—" : new Intl.NumberFormat("fr-CH", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v);
const SECTION_LABELS = {
  signals_detected: "Détecté par NOAM — signaux ouverts",
  obligations_due: "Échéances & obligations (7 jours)",
  deals_moved_24h: "Deals ayant bougé (24h)",
  decisions_moved_24h: "Décisions — tous Systems (24h)",
  gov_registry_24h: "Registre Governance (24h)",
  stale_deals: "Deals stagnants",
  open_decisions: "Décisions ouvertes",
  diverged_convictions: "Convictions divergentes",
  recent_outcomes: "Outcomes récents (7j)",
};

export const EVENT_LABELS = {
  phase_transition: "Transition de phase",
  decision_created: "Décision créée",
  decision_decided: "Décision tranchée",
  decision_reestimated: "Conviction ré-estimée",
  decision_outcome: "Outcome enregistré",
  party_added: "Partie ajoutée",
  document_linked: "Document lié",
  negotiation_created: "Négociation ouverte",
};

// W11 crédibilité — clés humanisées uniquement ; les clés techniques brutes sont masquées
const EVIDENCE_LABELS = {
  system: "System", verdict: "Verdict", adjusted_ev: "AdjEV",
  adjev_at_decision: "AdjEV à la décision", adjev_current: "AdjEV actuelle",
  estimation_version: "version", recommendation: "Suggestion",
  payoff_realized: "Réalisé", recorded_at: "le", last_update: "dernière maj",
  code: "Code", source: "Source", kind: "Type", blocking: "Bloquante",
};
const VALUE_LABELS = {
  deal: "Deal", financial_markets: "Financial Markets", governance: "Governance", platform: "Platform",
  condition_precedent: "Condition suspensive", approval: "Approbation",
  required_document: "Document requis", obligation: "Obligation", action: "Action",
};

function linkFor(obj) {
  if (!obj) return null;
  if (obj.type === "deal" && obj.id) return `/app/deals/${obj.id}`;
  if (obj.type === "decision") return obj.deal_id ? `/app/deals/${obj.deal_id}` : "/app/decisions";
  if (obj.type === "obligation") return obj.deal_id ? `/app/deals/${obj.deal_id}` : null;
  return null;
}

export function ActionDigest({ onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/workspace/action-digest").then((r) => setData(r.data)).catch((e) => notify.error(fmtError(e)));
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div className="noam-card w-full max-w-2xl p-5 mt-10 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="action-digest-modal">
        <div className="flex items-center gap-2">
          <Newspaper className="w-4 h-4 text-[#378ADD]" />
          <span className="text-sm font-semibold text-zinc-100 flex-1">Digest du jour — faits journalisés</span>
          <button onClick={onClose} className="btn-ghost p-1" data-testid="action-digest-close"><X className="w-4 h-4" /></button>
        </div>
        {!data && <div className="flex justify-center py-10 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>}
        {data && data.empty && (
          <div className="p-8 text-center text-xs text-zinc-600 border border-dashed border-white/10 rounded-lg" data-testid="action-digest-empty">
            Rien à signaler : aucun fait nouveau journalisé. NOAM ne remplit jamais un digest artificiellement.
          </div>
        )}
        {data && !data.empty && Object.entries(data.sections).map(([key, items]) => items.length > 0 && (
          <div key={key} className="space-y-1">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{SECTION_LABELS[key]} ({items.length})</div>
            {items.map((it, i) => {
              const to = linkFor(it.object);
              const mainLabel = it.action || EVENT_LABELS[it.type] || it.type || (it.object || {}).title;
              const body = (
                <div className="p-2 rounded bg-white/[0.03] hover:bg-white/[0.06] transition-colors text-xs space-y-0.5" data-testid={`digest-item-${key}-${i}`}>
                  <div className="text-zinc-200">{mainLabel}</div>
                  <div className="text-[10px] text-zinc-500">{it.why}</div>
                  <div className="text-[10px] text-zinc-600">
                    {(it.object || {}).title && it.object.title !== mainLabel && <span>{it.object.title} · </span>}
                    {it.evidence && Object.entries(it.evidence)
                      .filter(([k, v]) => v != null && EVIDENCE_LABELS[k.replace(/_chf$/, "")])
                      .slice(0, 3).map(([k, v]) => {
                        const cur = (it.evidence || {}).currency || "CHF";
                        const isDate = typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v);
                        const shown = isDate ? new Date(v).toLocaleDateString("fr-CH")
                          : k.includes("chf") ? fmtMoney(v, cur)
                          : VALUE_LABELS[v] || String(v).slice(0, 40);
                        return <span key={k} className="mr-2">{EVIDENCE_LABELS[k.replace(/_chf$/, "")]} : {shown}</span>;
                      })}
                  </div>
                </div>
              );
              return to ? <Link key={i} to={to} onClick={onClose} className="block">{body}</Link> : <div key={i}>{body}</div>;
            })}
          </div>
        ))}
        {data && <div className="text-[9px] text-zinc-600">{data.note} · généré {new Date(data.generated_at).toLocaleString("fr-CH")}</div>}
      </div>
    </div>
  );
}
